import { NgClass, NgStyle } from "@angular/common";
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild, inject } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { TranslateModule } from "@ngx-translate/core";
import { TilbyGesturesDirective } from "@tilby/tilby-ui-lib/directives/tilby-gestures";
import { Categories } from "tilby-models";
import { CashregisterStateService, Section } from "src/app/features";

@Component({
    selector: "app-category-tab-v2",
    templateUrl: "./category-tab-v2.component.html",
    standalone: true,
    imports: [NgClass, NgStyle, MatIconModule, TilbyGesturesDirective, TranslateModule]
})
export class CategoryTabV2Component implements AfterViewInit, OnChanges {
    private readonly cashregisterStateService = inject(CashregisterStateService);

    private static readonly defaultColor = "b2dfdb";
    private static readonly FAVORITES_CATEGORY = { color: "90caf9" } as Categories;
    private static readonly MAX_SCALE = 1.06;
    private static readonly MAX_GROWTH = 6;
    private static readonly SCROLL_STEP = 300;

    @ViewChild('scroller', { static: true }) private readonly scrollerRef!: ElementRef<HTMLElement>;
    @Input({ required: true }) categoriesSet!: Categories[];
    @Input() itemsFavorite = false;
    @Input() initCategorySelected?: Categories;
    @Input() fontNameSize = 12;
    @Output() categorySelected = new EventEmitter<Categories>();
    @Output() goToSection = new EventEmitter<Section>();

    protected selectedIndex = 0;
    protected canScrollBefore = false;
    protected canScrollAfter = false;
    protected selectedScale = CategoryTabV2Component.MAX_SCALE;

    ngAfterViewInit(): void {
        const index = this.initCategorySelected ? this.categoriesSet.findIndex(category => category.id === this.initCategorySelected!.id) : -1;
        this.selectedIndex = this.itemsFavorite ? (index + 1) : index;
        setTimeout(() => {
            this.updateSelectedScale();
            this.scrollSelectedIntoView();
            this.updateScrollState();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.categoriesSet || changes.itemsFavorite || changes.fontNameSize) {
            setTimeout(() => {
                this.updateSelectedScale();
                this.updateScrollState();
            });
        }
    }

    @HostListener('window:resize')
    protected updateScrollState(): void {
        const el = this.scrollerRef.nativeElement;
        this.canScrollBefore = el.scrollLeft > 0;
        this.canScrollAfter = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    }

    protected categoryColor(category: Categories): string {
        return `#${category.color || CategoryTabV2Component.defaultColor}`;
    }

    /** Sfondo della card */
    protected categoryBackground(category: Categories, selected = false): string {
        const color = this.categoryColor(category);
        return selected ? color : `linear-gradient(135deg, ${color}59 0%, ${color}0d 100%), var(--sys-card)`;
    }

    /** Bordo della card */
    protected categoryBorder(category: Categories, selected = false): string {
        return `${this.categoryColor(category)}${selected ? '' : '8c'}`;
    }

    /** Ombra della card */
    protected categoryShadow(category: Categories, selected = false): string {
        return selected
            ? `0 0 0 0 ${this.categoryColor(category)}b3, 0 1px 3px rgba(38, 38, 38, 0.25)`
            : '0 1px 2px var(--sys-cashregister-shadow-color)';
    }

    /** Ombra del pulsante preferiti */
    protected favoritesBackground(selected: boolean): string {
        // return this.categoryBackground(CategoryTabV2Component.FAVORITES_CATEGORY, selected);
        const color =  this.categoryColor(CategoryTabV2Component.FAVORITES_CATEGORY);
        return selected ? `${color}9c` : 
            `linear-gradient(135deg, ${color}59 0%, ${color}0d 100%), var(--sys-card)`;
    }

    protected favoritesBorder(selected: boolean): string {
        // return this.categoryBorder(CategoryTabV2Component.FAVORITES_CATEGORY, selected);
        return `${this.categoryColor(CategoryTabV2Component.FAVORITES_CATEGORY)}${selected ? '' : '8c'}`
    }

    protected favoritesShadow(selected: boolean): string {
        return this.categoryShadow(CategoryTabV2Component.FAVORITES_CATEGORY, selected);
    }

    /** Stella blu a riposo, nera se selezionata */
    protected favoritesIconColor(selected: boolean): string {
        return selected ? '#262626' : '#1a73e8';
    }

    /** Swipe verso sinistra */
    protected swipeLeft(): void {
        this.scrollBy(1);
    }

    /** Swipe verso destra */
    protected swipeRight(): void {
        this.scrollBy(-1);
    }

    protected scrollBy(direction: 1 | -1): void {
        this.scrollerRef.nativeElement.scrollBy({ left: direction * CategoryTabV2Component.SCROLL_STEP, behavior: 'smooth' });
    }

    selectByIndex(idx: number): void {
        if (idx !== this.selectedIndex) {
            this.selectedIndex = idx;
            this.updateSelectedScale();
            this.emitCategorySelection(idx);
            setTimeout(() => this.scrollSelectedIntoView());
        }
    }

    protected onTabClick(idx: number): void {
        if (this.onCategoryTabClick(idx)) {
            return;
        }
        this.selectByIndex(idx);
    }

    private onCategoryTabClick(idx: number): boolean {
        const index = this.itemsFavorite ? (idx - 1) : idx;

        if (index === -1) {
            return false;
        }

        const amount = this.cashregisterStateService.keyboardTotalValue();
        const currentPriceList = this.cashregisterStateService.priceList();
        const category = this.categoriesSet[index];

        if (amount && category[`department${currentPriceList == 1 ? '' : currentPriceList}_id` as keyof Categories]) {
            this.cashregisterStateService.addDynamicCategoryItemToSale$.next({
                price: amount,
                category: category
            });

            return true;
        }

        return false;
    }

    private emitCategorySelection(idx: number): void {
        const index = this.itemsFavorite ? (idx - 1) : idx;

        if (index === -1) {
            this.goToSection.emit('favorites');
        } else {
            this.categorySelected.emit(this.categoriesSet[index]);
        }
    }

    /**
     * Scale della scheda selezionata
     */
    private updateSelectedScale(): void {
        const chip = this.scrollerRef.nativeElement.querySelector<HTMLElement>(`[data-tab-index="${this.selectedIndex}"]`);
        const width = chip?.offsetWidth ?? 0;
        this.selectedScale = width
            ? Math.min(CategoryTabV2Component.MAX_SCALE, 1 + CategoryTabV2Component.MAX_GROWTH / width)
            : CategoryTabV2Component.MAX_SCALE;
    }

    private scrollSelectedIntoView(): void {
        this.scrollerRef.nativeElement
            .querySelector<HTMLElement>(`[data-tab-index="${this.selectedIndex}"]`)
            ?.scrollIntoView({ inline: 'center', block: 'nearest' });
    }
}
